import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiUsers,
  FiCpu,
  FiCommand,
  FiActivity,
  FiSun,
  FiMoon,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { useTheme } from "./context/ThemeContext";
import logo from "./image/logo3.png";

function Welcome() {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoveredCard, setHoveredCard] = useState(null);
  const statsCardRef = useRef(null);
  const [statsProgress, setStatsProgress] = useState(0);
  const [particlePositions, setParticlePositions] = useState([]);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    setMounted(true);

    // Animate stats progress bar
    const progressInterval = setInterval(() => {
      setStatsProgress((prev) => {
        if (prev >= 75) {
          clearInterval(progressInterval);
          return 75;
        }
        return prev + 1;
      });
    }, 20);

    // Generate particle positions for ambient effect
    const particles = Array.from({ length: 8 }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 3 + Math.random() * 2,
    }));
    setParticlePositions(particles);

    return () => clearInterval(progressInterval);
  }, []);

  const handleStart = () => {
    navigate("/CreateSociety");
  };

  const handleMouseMove = (e, cardIndex) => {
    if (!hoveredCard) return;

    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setMousePosition({ x, y });
  };

  const menuItems = [
    {
      label: "Societies",
      icon: <HiOutlineBuildingOffice2 />,
      path: "/SocietyList",
      color: "text-blue-400",
      hoverColor: "hover:text-blue-300",
      glowColor: "rgba(96, 165, 250, 0.3)",
      count: "250+",
      status: "active",
    },
    {
      label: "Admins",
      icon: <FiUsers />,
      path: "/CreateSocietyAdmin",
      color: "text-purple-400",
      hoverColor: "hover:text-purple-300",
      glowColor: "rgba(192, 132, 252, 0.3)",
      count: "48",
      status: "verified",
    },
    {
      label: "System Logs",
      icon: <FiActivity />,
      path: "/all-admin-login",
      color: "text-signal-orange",
      hoverColor: "hover:text-orange-300",
      glowColor: "rgba(251, 146, 60, 0.3)",
      count: "1.2k",
      status: "monitoring",
    },
  ];

  return (
    <div className="min-h-screen bg-bg-primary text-text-main font-satoshi overflow-hidden relative selection:bg-signal-orange selection:text-noir-900 transition-colors duration-300 scale-90 origin-top">
      {/* Noise Texture Overlay */}
      <div className="fixed inset-0 opacity-[0.02] pointer-events-none z-50 bg-noise animate-grain"></div>

      {/* Animated Background Particles */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        {particlePositions.map((particle, idx) => (
          <div
            key={idx}
            className="absolute w-0.5 h-0.5 bg-signal-lime rounded-full opacity-20"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              animation: `float ${particle.duration}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
            }}
          />
        ))}
        <div className="absolute top-[-20%] left-[-10%] w-[40vw] h-[40vw] bg-bg-tertiary rounded-full blur-[80px] opacity-20 animate-pulse-slow"></div>
        <div
          className="absolute bottom-[-10%] right-[-10%] w-[30vw] h-[30vw] bg-bg-secondary rounded-full blur-[60px] opacity-20 animate-pulse-slow"
          style={{ animationDelay: "1s" }}
        ></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 h-screen flex flex-col justify-between">
        {/* Header */}
        <header
          className={`flex justify-between items-center transition-all duration-700 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}
        >
          <div className="flex items-center gap-2 group cursor-pointer">
            <div className="w-16 h-6 flex items-center justify-center transition-all duration-300 group-hover:scale-105">
              <img
                src={logo}
                alt="Admin Dashboard"
                className="relative z-10 w-20 drop-shadow-xl"
              />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="hidden md:flex items-center gap-6 text-xs bg-red-400 px-2 py-1 rounded text-white font-medium">
              <span className="flex items-center gap-2 group cursor-default">
                <span className="group-hover:text-signal-lime transition-colors duration-300">
                  Logout
                </span>
              </span>
            </div>
            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-full border border-border hover:bg-bg-tertiary transition-colors text-text-main hover:text-signal-orange text-sm"
              title="Toggle Theme"
            >
              {theme === "dark" ? <FiMoon /> : <FiSun />}
            </button>
          </div>
        </header>

        {/* Main Grid Content */}
        <main className="grid lg:grid-cols-12 gap-8 lg:gap-6 items-center mt-6 lg:mt-0">
          {/* Typography Hero (Left) */}
          <div className="lg:col-span-7 space-y-6">
            <div className="overflow-hidden">
              <h1 className="font-clash font-semibold text-6xl md:text-6xl lg:text-5xl leading-[0.9] tracking-tight uppercase text-text-main">
                <span
                  className={`block transition-transform text-8xl duration-700 delay-100 hover:translate-x-2 cursor-default ${mounted ? "translate-y-0" : "translate-y-full"}`}
                >
                  Society
                </span>
                <span
                  className={`block text-transparent bg-clip-text text-8xl bg-gradient-to-r from-text-main to-text-muted transition-all duration-700 delay-200 hover:from-signal-orange hover:to-yellow-500 cursor-default ${mounted ? "translate-y-0" : "translate-y-full"}`}
                >
                  Control
                </span>
              </h1>
            </div>

            <div
              className={`flex flex-wrap gap-3 pt-3 transition-all duration-700 delay-500 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
            >
              <button
                onClick={handleStart}
                className="group relative px-6 py-3 bg-yellow-400 text-noir-900 font-bold hover:bg-signal-orange transition-all duration-300 ease-out flex items-center gap-2 overflow-hidden text-sm"
              >
                {/* Shimmer effect */}
                <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transform -skew-x-12 group-hover:animate-shimmer"></span>

                {/* Button content */}
                <span className="relative z-10 font-clash uppercase tracking-wider text-black group-hover:tracking-widest transition-all duration-300">
                  Initialize System
                </span>
                <FiArrowRight className="relative z-10 text-lg transition-all duration-300 group-hover:translate-x-2 text-black group-hover:scale-110" />

                {/* Corner accents */}
                <span className="absolute top-0 left-0 w-1.5 h-1.5 border-t border-l border-noir-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <span className="absolute bottom-0 right-0 w-1.5 h-1.5 border-b border-r border-noir-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </button>
            </div>
          </div>

          {/* Asymmetric Functional Grid (Right) */}
          <div
            className={`lg:col-span-5 grid grid-cols-2 gap-3 transition-all duration-700 delay-300 ${mounted ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
          >
            {/* Main Stats Card */}
            <div
              ref={statsCardRef}
              className="col-span-2 bg-bg-secondary/50 backdrop-blur-sm border border-border p-4 hover:border-signal-lime/50 transition-all duration-400 group relative overflow-hidden cursor-default"
              onMouseEnter={() => setHoveredCard("stats")}
              onMouseLeave={() => setHoveredCard(null)}
              onMouseMove={(e) => handleMouseMove(e, "stats")}
            >
              {/* Gradient glow on hover */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none"
                style={{
                  background:
                    hoveredCard === "stats"
                      ? `radial-gradient(400px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(132, 204, 22, 0.1), transparent 40%)`
                      : "none",
                }}
              />

              <div className="flex justify-between items-start mb-4 relative z-10">
                <FiCpu className="text-2xl text-text-muted group-hover:text-signal-lime transition-all duration-400 group-hover:rotate-90 group-hover:scale-105" />
              </div>

              <div className="space-y-1 relative z-10">
                <div className="text-3xl font-clash font-bold transition-all duration-300 text-text-main group-hover:text-signal-lime group-hover:scale-105 transform origin-left">
                  250+
                </div>
                <div className="text-xs text-text-muted uppercase tracking-wider group-hover:tracking-widest transition-all duration-300">
                  Active Societies
                </div>
              </div>

              <div className="mt-3 h-1 w-full bg-bg-tertiary overflow-hidden rounded-full relative z-10">
                <div
                  className="h-full bg-gradient-to-r from-signal-lime to-yellow-400 transition-all duration-700 ease-out relative"
                  style={{ width: `${statsProgress}%` }}
                >
                  {/* Animated shine */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer-slow"></div>
                </div>
              </div>

              {/* Pulse effect on corners */}
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-signal-lime opacity-0 group-hover:opacity-100 transition-opacity duration-400"></div>
              <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-signal-lime opacity-0 group-hover:opacity-100 transition-opacity duration-400"></div>
            </div>

            {/* Quick Actions Grid */}
            {menuItems.map((item, idx) => (
              <div
                key={idx}
                onClick={() => navigate(item.path)}
                onMouseEnter={() => setHoveredCard(idx)}
                onMouseLeave={() => setHoveredCard(null)}
                onMouseMove={(e) => handleMouseMove(e, idx)}
                className={`
                  ${idx === 2 ? "col-span-2 aspect-auto flex-row items-center gap-3" : "aspect-square flex-col justify-between"} 
                  p-4 bg-bg-secondary/50 backdrop-blur-sm border border-border hover:border-border/60
                  cursor-pointer transition-all duration-400 group flex relative overflow-hidden
                  hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] text-sm
                `}
                style={{
                  boxShadow:
                    hoveredCard === idx ? `0 0 30px ${item.glowColor}` : "none",
                }}
              >
                {/* Animated gradient background */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                  style={{
                    background:
                      hoveredCard === idx
                        ? `radial-gradient(300px circle at ${mousePosition.x}% ${mousePosition.y}%, ${item.glowColor}, transparent 60%)`
                        : "none",
                  }}
                />

                {/* Scan line effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-transparent animate-scan"></div>
                </div>

                {/* Icon container */}
                <div className="relative z-10">
                  <div
                    className={`${idx === 2 ? "text-xl" : "text-2xl"} ${item.color} group-hover:scale-110 transition-all duration-400 group-hover:rotate-12 relative`}
                  >
                    {item.icon}
                    {/* Icon glow */}
                    <div
                      className={`absolute inset-0 ${item.color} blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-400`}
                    >
                      {item.icon}
                    </div>
                  </div>
                </div>

                {/* Text content */}
                <div
                  className={`font-medium text-xs ${idx === 2 ? "" : "flex-1 flex flex-col justify-end"}`}
                >
                  <span
                    className={`block text-text-muted text-[9px] uppercase tracking-widest mb-1 transition-all duration-300 ${item.hoverColor} group-hover:tracking-[0.2em]`}
                  >
                    Route 0{idx + 1}
                  </span>
                  <span className="text-text-main group-hover:text-text-main transition-colors duration-300">
                    {item.label}
                  </span>

                  {/* Metadata badge */}
                  {idx !== 2 && (
                    <div className="mt-2 pt-2 border-t border-border group-hover:border-border/60 transition-colors duration-300">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-text-muted group-hover:text-text-main transition-colors duration-300 font-mono">
                          {item.count}
                        </span>
                        <span
                          className={`px-1.5 py-0.5 rounded-full text-[8px] uppercase tracking-wider border ${item.color.replace("text-", "border-")} ${item.color} opacity-60 group-hover:opacity-100 transition-opacity duration-300`}
                        >
                          {item.status}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Arrow for system logs */}
                {idx === 2 && (
                  <FiArrowRight className="ml-auto text-text-muted group-hover:translate-x-1.5 group-hover:text-signal-orange transition-all duration-300 relative z-10 group-hover:scale-110" />
                )}

                {/* Corner brackets */}
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-transparent group-hover:border-white/50 transition-all duration-300"></div>
                <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-transparent group-hover:border-white/50 transition-all duration-300"></div>

                {/* Ripple effect on click */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute inset-0 bg-white rounded-full transform scale-0 group-active:scale-150 opacity-20 transition-transform duration-400"></div>
                </div>
              </div>
            ))}
          </div>
        </main>

        {/* Footer info */}
        <footer
          className={`flex justify-between items-end border-t border-border pt-4 transition-all duration-700 delay-700 ${mounted ? "opacity-100" : "opacity-0"}`}
        >
          <div className="text-[10px] text-text-muted font-mono space-y-0.5">
            <p className="hover:text-signal-lime transition-colors duration-300 cursor-default flex items-center gap-1.5">
              <span className="w-1 h-1 bg-signal-lime rounded-full animate-pulse"></span>
              SECURE CONNECTION :: ESTABLISHED
            </p>
            <p className="hover:text-signal-lime transition-colors duration-300 cursor-default flex items-center gap-1.5">
              <span
                className="w-1 h-1 bg-blue-400 rounded-full animate-pulse"
                style={{ animationDelay: "0.5s" }}
              ></span>
              LATENCY :: 24ms
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-text-muted uppercase tracking-widest hover:tracking-[0.2em] transition-all duration-300">
              Powered by{" "}
              <span className="text-text-main font-bold hover:text-signal-orange transition-colors duration-300 cursor-default">
                ZAP SOLUTIONZ
              </span>
            </p>
          </div>
        </footer>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-15px) translateX(8px);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%) skewX(-12deg);
          }
          100% {
            transform: translateX(200%) skewX(-12deg);
          }
        }

        @keyframes shimmer-slow {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes scan {
          0% {
            transform: translateY(-100%);
          }
          100% {
            transform: translateY(100%);
          }
        }

        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.05);
          }
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        .animate-shimmer-slow {
          animation: shimmer-slow 3s infinite;
        }

        .animate-scan {
          animation: scan 3s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default Welcome;
